// src/pages/SugarMeasurements.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { sugarMeasurementService } from '../services/sugarMeasurementService';
import { userService } from '../services/userService';

const SugarMeasurements = () => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
    mood: ''
  });
  const [stats, setStats] = useState({
    average: 0,
    min: 0,
    max: 0,
    count: 0
  });
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userId } = useParams(); // Get userId from URL parameters
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user details first
        if (userId) {
          try {
            const userInfo = await userService.getUserById(userId);
            console.log('User details:', userInfo);
            setUserDetails(userInfo);
          } catch (userError) {
            console.error('Error fetching user details:', userError);
            setError('Failed to fetch user details: ' + userError.message);
            setLoading(false);
            return;
          }
        }
        
        // Fetch measurements for the specified user
        let data;
        if (userId) {
          // Use the endpoint that filters by userId on the server
          data = await sugarMeasurementService.getMeasurementsByUserId(userId);
        } else {
          // If no userId provided, fetch all measurements (admin view)
          data = await sugarMeasurementService.getMeasurements();
        }
        
        setMeasurements(data);
        calculateStats(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch measurements: ' + err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId]); // Re-run when userId changes
  
  const calculateStats = (data) => {
    if (!data || data.length === 0) {
      setStats({
        average: 0,
        min: 0,
        max: 0,
        count: 0
      });
      return;
    }
    
    const sugarLevels = data.map(m => m.sugarLevel);
    const avg = sugarLevels.reduce((a, b) => a + b, 0) / data.length;
    
    setStats({
      average: parseFloat(avg.toFixed(1)),
      min: Math.min(...sugarLevels),
      max: Math.max(...sugarLevels),
      count: data.length
    });
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  const filteredMeasurements = measurements.filter(measurement => {
    // Date filter
    if (filters.dateFrom && new Date(measurement.datetime) < new Date(filters.dateFrom)) {
      return false;
    }
    if (filters.dateTo && new Date(measurement.datetime) > new Date(filters.dateTo + 'T23:59:59')) {
      return false;
    }
    
    // Status filter
    if (filters.status && measurement.status !== filters.status) {
      return false;
    }
    
    // Mood filter
    if (filters.mood && measurement.mood !== filters.mood) {
      return false;
    }
    
    return true;
  });
  
  // Get unique statuses and moods for filter dropdowns
  const statuses = [...new Set(measurements.map(m => m.status))];
  const moods = [...new Set(measurements.filter(m => m.mood).map(m => m.mood))];
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'high':
        return 'high-status';
      case 'low':
        return 'low-status';
      case 'normal':
        return 'normal-status';
      default:
        return '';
    }
  };
  
  return (
    <div className="login-container" style={{ maxWidth: '1200px' }}>
      <h1>
        {userDetails 
          ? `Sugar Measurements for ${userDetails.name}`
          : userId 
            ? `Sugar Measurements for User #${userId}`
            : 'All Sugar Measurements'
        }
      </h1>
      
      {/* Stats Cards */}
      <div className="stats-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
        <div className="stat-card" style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Average Sugar Level</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.average} mmol/L</div>
        </div>
        <div className="stat-card" style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Lowest Reading</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.min} mmol/L</div>
        </div>
        <div className="stat-card" style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Highest Reading</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.max} mmol/L</div>
        </div>
        <div className="stat-card" style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Total Measurements</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.count}</div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="filters-container" style={{ padding: '15px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '20px' }}>
        <h2 style={{ marginBottom: '15px' }}>Filters</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>From Date</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>To Date</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>Mood</label>
            <select
              name="mood"
              value={filters.mood}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">All Moods</option>
              {moods.map(mood => (
                <option key={mood} value={mood}>{mood}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {error && <div className="error">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading measurements...</div>
      ) : (
        <>
          {/* Measurements Table */}
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="measurements-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Sugar Level</th>
                  <th>Status</th>
                  <th>Insulin</th>
                  <th>Mood</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredMeasurements.length > 0 ? (
                  filteredMeasurements.map((measurement) => (
                    <tr key={measurement.id}>
                      <td>{formatDate(measurement.datetime)}</td>
                      <td><strong>{measurement.sugarLevel} mmol/L</strong></td>
                      <td className={getStatusColor(measurement.status)}>{measurement.status}</td>
                      <td>{measurement.insulin ? `${measurement.insulin} units` : '-'}</td>
                      <td>{measurement.mood || '-'}</td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {measurement.notes || '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center' }}>No measurements found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div style={{ marginTop: '10px', textAlign: 'right' }}>
            Showing {filteredMeasurements.length} of {measurements.length} measurements
          </div>
        </>
      )}
      
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <button 
          onClick={() => navigate('/dashboard')}
          className="secondary-button"
        >
          Back to Dashboard
        </button>
        
        {/* Only show Add button if viewing own measurements or if admin */}
        {(!userId || userId === user?.id || user?.role === 'admin') && (
          <button 
            onClick={() => navigate(userId ? `/add-measurement/${userId}` : '/add-measurement')}
            className="primary-button"
          >
            Add New Measurement
          </button>
        )}
      </div>
    </div>
  );
};

export default SugarMeasurements;